import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {ClickEventHandler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChildrenCell} from '../../components/common/table/cells/childrenCell';
import {DateCell} from '../../components/common/table/cells/dateCell';
import {EmptyCell} from '../../components/common/table/cells/emptyCell';
import {TableColumn} from '../../components/common/table/tableColumn';
import {TableRow, TableRowRenderOptions} from '../../components/common/table/tableRow';
import {TagModel} from '../../models/api/tagModel';
import {ResourceMap} from '../../store/appTypes';
import {SettingsResourcesTableColumnsEnum} from '../common/settingsResourcesCommon';
import {SettingsTagInboxesCellContainer} from './settingsTagInboxesCell';
import {SettingsTagNameCellInheritableProps} from './settingsTagNameCell';
import {SettingsTagNameCellContainer} from './settingsTagNameCellContainer';

/*
 * Props.
 */

interface SettingsTagsTableRowProps extends SettingsTagNameCellInheritableProps {
  isSelected: boolean;
  tableColumns: ReadonlyArray<TableColumn<SettingsResourcesTableColumnsEnum>>;
  options: TableRowRenderOptions;
  isPreviousSiblingSelected: boolean;
  isNextSiblingSelected: boolean;
  isInMultiSelectMode?: boolean;
  onNameCellClick: ClickEventHandler;
  onCellClick: ClickEventHandler;
  onCheckBoxToggle: ClickEventHandler;
  tag: TagModel;
  searchTagsIdMap: ResourceMap<TagModel>;
  onDeleteSingleTagClick: HandlerOf<TagModel>;
  selectedTagIds: ReadonlyArray<number>;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const SettingsTagsTableRow: FC<SettingsTagsTableRowProps> = (props) => {
  const {
    isSelected,
    tableColumns,
    options,
    isPreviousSiblingSelected,
    isNextSiblingSelected,
    isInMultiSelectMode,
    onCheckBoxToggle,
    onDeleteSingleTagClick,
    selectedTagIds,
    tag,
  } = props;

  const cellRenderer = (
    index: number,
    column: TableColumn<SettingsResourcesTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    const emptyCell = (
      <EmptyCell
        key={column.type}
        settings={column.settings}
        isHovering={isHovering}
        size={options.size}
        index={index}
        isSelected={isSelected}
        shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
        shouldRemoveBottomBorderRadius={isNextSiblingSelected}
      />
    );
    switch (column.type) {
      case SettingsResourcesTableColumnsEnum.NAME:
      case SettingsResourcesTableColumnsEnum.PATH:
        return (
          <SettingsTagNameCellContainer
            searchTagsIdMap={props.searchTagsIdMap}
            isInSearch={props.isInSearch}
            isInArchivedView={props.isInArchivedView}
            hasChevron={props.hasChevron}
            isExpanded={props.isExpanded}
            hasHover={props.hasHover}
            onChevronClick={props.onChevronClick}
            renderActionsDropdown={props.renderActionsDropdown}
            onActionsDropdownOpened={props.onActionsDropdownOpened}
            key={column.id}
            value={tag}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            onClick={(e) => {
              e.preventDefault();
              props.onNameCellClick(e);
            }}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );

      case SettingsResourcesTableColumnsEnum.AVAILABLE_IN: {
        return (
          <SettingsTagInboxesCellContainer
            key={column.id}
            value={tag}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      }

      case SettingsResourcesTableColumnsEnum.DATE_CREATED: {
        return (
          <DateCell
            key={column.id}
            value={tag.createdAt}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
            customFormat={{
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }}
          />
        );
      }
      case SettingsResourcesTableColumnsEnum.LAST_USED: {
        return (
          <DateCell
            key={column.id}
            value={tag.lastUsedAt}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
            customFormat={{
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }}
          />
        );
      }

      case SettingsResourcesTableColumnsEnum.ARCHIVED_DELETE: {
        return (
          <ChildrenCell
            key={column.id}
            value={tag.lastUsedAt}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            isSelected={isSelected}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
            shouldShowStickyGradient={shouldShowStickyGradient}
          >
            <Button
              isDisabled={selectedTagIds.length > 0}
              variant={ButtonVariantsEnum.SECONDARY_DANGER}
              onClick={() => onDeleteSingleTagClick(tag)}
              shouldUseDirectHover
            >
              <Trans id="settingstags-bulk-delete">Delete</Trans>
            </Button>
          </ChildrenCell>
        );
      }

      default: {
        return emptyCell;
      }
    }
  };

  return (
    <StyledTableRow
      hasHover
      isSelected={isSelected}
      onCheckBoxClick={(e) => {
        e.preventDefault();
        onCheckBoxToggle(e);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      onSelect={(e) => {
        if (e.defaultPrevented) {
          return;
        }

        props.onCellClick(e);
      }}
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};
